import { useEffect, useId, useRef, useState } from "react";
import { videoEmbed } from "@/lib/api";

export default function VideoPlayer({ url, title = "Video NAWADAH", poster = "", className = "", autoPlay = false, muted = false, controls = true, onEnded, onError }) {
  const [failed, setFailed] = useState(false);
  const iframeRef = useRef(null);
  const playerId = useId().replaceAll(":", "");
  const media = videoEmbed(url);
  useEffect(() => setFailed(false), [url]);

  useEffect(() => {
    if (media?.type !== "youtube") return undefined;

    const notify = (active) => window.dispatchEvent(new CustomEvent("nawadah:video-state", {
      detail: { id: playerId, active },
    }));
    const listen = () => iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: playerId }), "*");
    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      let data = event.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== "object") return;
      if (data.event === "onReady") listen();
      if (data.event === "onStateChange") notify(Number(data.info) === 1 && !muted);
      if (data.event === "infoDelivery" && data.info && "playerState" in data.info) {
        const isMuted = data.info.muted ?? muted;
        const volume = data.info.volume ?? 100;
        notify(Number(data.info.playerState) === 1 && !isMuted && Number(volume) > 0);
      }
    };

    window.addEventListener("message", handleMessage);
    const timer = window.setInterval(listen, 1500);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("message", handleMessage);
      notify(false);
    };
  }, [media?.src, media?.type, muted, playerId]);

  if (!media) return null;

  if (failed) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-gradient-to-br from-[#061426] to-[#102d4d] px-6 text-center text-slate-200`} role="status">
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-gold/60 text-lg font-bold text-gold">N</span>
        <span className="text-sm font-semibold">Video sedang dipulihkan</span>
        <span className="mt-1 text-xs text-slate-400">Konten asli akan tampil otomatis setelah pemindahan selesai.</span>
      </div>
    );
  }

  if (media.type === "youtube") {
    const separator = media.src.includes("?") ? "&" : "?";
    const params = new URLSearchParams({
      enablejsapi: "1",
      playsinline: "1",
    });
    if (autoPlay) params.set("autoplay", "1");
    if (muted) params.set("mute", "1");
    const src = `${media.src}${separator}${params.toString()}`;
    return (
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        className={className}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  const handleError = (event) => {
    setFailed(true);
    onError?.(event);
  };

  return <video src={media.src} poster={poster || undefined} controls={controls} autoPlay={autoPlay} muted={muted} playsInline onEnded={onEnded} onError={handleError} className={className} />;
}
